import { create } from 'zustand';

import { TActiveCFSFilter } from '@/api';
import { IGetParams } from '@/api/types';
import { ICFS } from '@/models';

const CFS_NAVIGATION_KEY = 'cfs-navigation';

export type CFSNavigationSourceRoute = 'cfs-active' | 'cfs';

export interface ActiveCFSNavigationContext {
  sourceRoute: 'cfs-active';
  requestParams: IGetParams;
  userFilter: TActiveCFSFilter;
  agencyTypeAgencyIds?: string[];
}

export interface CFSListNavigationContext {
  sourceRoute: 'cfs';
  requestParams: IGetParams;
}

export type CFSNavigationContext =
  | ActiveCFSNavigationContext
  | CFSListNavigationContext;

export interface CFSListCacheEntry {
  rows: any[];
  rawDocs: ICFS[];
  totalCount: number;
  page: number;
  filter: string;
  sort: string;
  filtersKey: string;
}

export interface ActiveCFSListCacheEntry {
  list: ICFS[];
  userFilter: string; // JSON.stringify of TActiveCFSFilter
  sortBy: string;
  searchText: string;
}

interface CFSNavigationState {
  // List of CFS IDs in current navigation context
  cfsNavigationList: string[];
  // Source route to know which list we're navigating from
  sourceRoute: CFSNavigationSourceRoute | null;
  // Source list context needed to refresh the navigation set from detail pages
  navigationContext: CFSNavigationContext | null;
  // Cache of full CFS data for prefetching and avoiding refetches
  cfsCache: Map<string, ICFS>;
  // Cache of the last CFS list page result for instant restore on back navigation
  cfsListCache: CFSListCacheEntry | null;

  // Actions
  setCFSNavigationList: (
    list: { _id?: string }[],
    sourceRoute: CFSNavigationSourceRoute,
    navigationContext?: CFSNavigationContext,
  ) => void;
  setCFSNavigationIds: (
    ids: string[],
    sourceRoute: CFSNavigationSourceRoute,
    navigationContext?: CFSNavigationContext,
  ) => void;
  clearNavigation: () => void;

  // Cache actions
  setCFSCache: (cfsId: string, data: ICFS) => void;
  getCFSFromCache: (cfsId: string) => ICFS | undefined;
  invalidateCFS: (cfsId: string) => void;
  clearCFSCache: () => void;

  // List cache actions
  setCFSListCache: (entry: CFSListCacheEntry) => void;
  clearCFSListCache: () => void;

  // Active CFS list cache
  activeCFSListCache: ActiveCFSListCacheEntry | null;
  setActiveCFSListCache: (entry: ActiveCFSListCacheEntry) => void;

  // Navigation helpers
  getNextCFSId: (currentId: string) => string | null;
  getPreviousCFSId: (currentId: string) => string | null;
  getCurrentIndex: (currentId: string) => number;
  getTotalCount: () => number;
}

const initialState = {
  cfsNavigationList: [] as string[],
  sourceRoute: null as CFSNavigationSourceRoute | null,
  navigationContext: null as CFSNavigationContext | null,
  cfsCache: new Map<string, ICFS>(),
  cfsListCache: null as CFSListCacheEntry | null,
  activeCFSListCache: null as ActiveCFSListCacheEntry | null,
};

export const useCFSNavigationStore = create<CFSNavigationState>((set, get) => ({
  ...initialState,

  setCFSNavigationList: (list, sourceRoute, navigationContext) => {
    const ids = list
      .filter((item): item is { _id: string } => !!item._id)
      .map((item) => item._id);
    const nextNavigationContext =
      navigationContext ??
      (get().navigationContext?.sourceRoute === sourceRoute
        ? get().navigationContext
        : null);

    // Save to localStorage for cross-window state recovery
    localStorage.setItem(
      CFS_NAVIGATION_KEY,
      JSON.stringify({
        ids,
        sourceRoute,
        navigationContext: nextNavigationContext,
      }),
    );

    set({
      cfsNavigationList: ids,
      sourceRoute,
      navigationContext: nextNavigationContext,
    });
  },

  setCFSNavigationIds: (ids, sourceRoute, navigationContext) => {
    const nextNavigationContext =
      navigationContext ??
      (get().navigationContext?.sourceRoute === sourceRoute
        ? get().navigationContext
        : null);

    localStorage.setItem(
      CFS_NAVIGATION_KEY,
      JSON.stringify({
        ids,
        sourceRoute,
        navigationContext: nextNavigationContext,
      }),
    );

    set({
      cfsNavigationList: ids,
      sourceRoute,
      navigationContext: nextNavigationContext,
    });
  },

  clearNavigation: () => {
    set({ ...initialState, cfsCache: new Map<string, ICFS>() });
  },

  setCFSCache: (cfsId, data) => {
    const { cfsCache } = get();
    const newCache = new Map(cfsCache);
    newCache.set(cfsId, data);
    set({ cfsCache: newCache });
  },

  getCFSFromCache: (cfsId) => {
    return get().cfsCache.get(cfsId);
  },

  invalidateCFS: (cfsId) => {
    const { cfsCache } = get();
    if (cfsCache.has(cfsId)) {
      const newCache = new Map(cfsCache);
      newCache.delete(cfsId);
      set({ cfsCache: newCache });
    }
  },

  clearCFSCache: () => {
    set({ cfsCache: new Map<string, ICFS>() });
  },

  setCFSListCache: (entry) => {
    set({ cfsListCache: entry });
  },

  clearCFSListCache: () => {
    set({ cfsListCache: null });
  },

  setActiveCFSListCache: (entry) => {
    set({ activeCFSListCache: entry });
  },

  getNextCFSId: (currentId) => {
    const { cfsNavigationList } = get();
    const currentIndex = cfsNavigationList.indexOf(currentId);
    if (currentIndex === -1 || currentIndex === cfsNavigationList.length - 1) {
      return null;
    }
    return cfsNavigationList[currentIndex + 1];
  },

  getPreviousCFSId: (currentId) => {
    const { cfsNavigationList } = get();
    const currentIndex = cfsNavigationList.indexOf(currentId);
    if (currentIndex <= 0) {
      return null;
    }
    return cfsNavigationList[currentIndex - 1];
  },

  getCurrentIndex: (currentId) => {
    const { cfsNavigationList } = get();
    return cfsNavigationList.indexOf(currentId);
  },

  getTotalCount: () => {
    return get().cfsNavigationList.length;
  },
}));
